import type { StrokePoints } from "../handwriting/stroke-shape.js";

/**
 * Putting the guide and the ink on the pad's canvas.
 *
 * Apart from `handwriting-pad.ts` so that the pad is the strokes and the
 * pointer events and this is the paint. Both are small and neither wants the
 * other's reasons in it.
 *
 * **The guide is 米字格.** The paper Chinese characters are learnt on, a square
 * with its halves and its diagonals marked, and it is here for the reason it is
 * on the paper. A character drawn against it comes out centred and in
 * proportion. That helps the lookup as well as the reader, since where a stroke
 * sits carries more weight than how long it is. See `runWeights`.
 *
 * The colours come off the page rather than being written here, so the pad
 * follows the reader's light or dark scheme without this module knowing which
 * it is. `--cb-border` is the guide and `--cb-text` the ink, read from the
 * canvas's own computed style.
 */

/** What one draw needs. */
export interface PadDrawing {
  /** The canvas, already sized to its box in device pixels. */
  readonly canvas: HTMLCanvasElement;

  /** The strokes already drawn, in CSS pixels. */
  readonly strokes: readonly StrokePoints[];

  /** The stroke under the reader's finger, where there is one. */
  readonly drawing: StrokePoints | undefined;
}

/** How thick the ink is, as a fraction of the square's side. */
const inkWidth = 0.045;

/** How thick the guide is, in CSS pixels. */
const guideWidth = 1;

/** How long a dash of the guide is, and the gap after it, in CSS pixels. */
const guideDash = [4, 4];

/** One custom property of the canvas, or a fallback a canvas can still draw. */
function colour(
  styles: CSSStyleDeclaration,
  name: string,
  fallback: string,
): string {
  const value = styles.getPropertyValue(name).trim();

  return value === "" ? fallback : value;
}

/**
 * Draws the 米字格 the character is written against.
 *
 * **Every property it draws with is set here, including the two it does not
 * care about.** A canvas context carries its settings between draws, and the
 * ink below leaves `lineCap` and `lineJoin` round. The guide inherited them on
 * the second draw and every draw after it, so each of its dashes gained a
 * rounded end and the empty square came back 46 pixels heavier than it had
 * opened. Saving and restoring around the block does not help on its own,
 * because what is restored is whatever the last draw left.
 */
function drawGuide(
  context: CanvasRenderingContext2D,
  side: number,
  stroke: string,
): void {
  context.save();
  context.strokeStyle = stroke;
  context.lineWidth = guideWidth;
  context.lineCap = "butt";
  context.lineJoin = "miter";
  context.setLineDash(guideDash);
  context.beginPath();
  context.moveTo(side / 2, 0);
  context.lineTo(side / 2, side);
  context.moveTo(0, side / 2);
  context.lineTo(side, side / 2);
  context.moveTo(0, 0);
  context.lineTo(side, side);
  context.moveTo(side, 0);
  context.lineTo(0, side);
  context.stroke();
  context.restore();
}

/**
 * Draws one stroke.
 *
 * The first point is moved to and then drawn to, which is what puts a dot on
 * the square for a 点 the reader tapped rather than flicked. A round line cap
 * over a segment of no length is a circle, and a stroke of one point that drew
 * nothing would be one the reader made and cannot see.
 */
function drawStroke(
  context: CanvasRenderingContext2D,
  stroke: StrokePoints,
): void {
  context.beginPath();

  for (const [at, [x, y]] of stroke.entries()) {
    if (at === 0) {
      context.moveTo(x, y);
    }

    context.lineTo(x, y);
  }

  context.stroke();
}

/** Clears the canvas and draws the guide and every stroke on it. */
export function drawPad(drawing: PadDrawing): void {
  const { canvas, strokes } = drawing;
  const context = canvas.getContext("2d");
  const view = canvas.ownerDocument.defaultView;

  if (context === null || view === null) {
    return;
  }

  const ratio = view.devicePixelRatio;
  const side = canvas.width / ratio;
  const styles = view.getComputedStyle(canvas);

  context.setTransform(ratio, 0, 0, ratio, 0, 0);
  context.clearRect(0, 0, side, canvas.height / ratio);
  drawGuide(context, side, colour(styles, "--cb-border", "#d0d0d0"));

  context.strokeStyle = colour(styles, "--cb-text", "#202020");
  context.lineWidth = side * inkWidth;
  context.lineCap = "round";
  context.lineJoin = "round";

  for (const stroke of strokes) {
    drawStroke(context, stroke);
  }

  if (drawing.drawing !== undefined) {
    drawStroke(context, drawing.drawing);
  }
}
